import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Mutation feedback.
 *
 * <p>Every mutation reports its outcome. A save that appears to do nothing is indistinguishable
 * from a save that failed, and an operator who cannot tell will try again — which for a status
 * change or a budget edit is not harmless.
 */

type ToastTone = 'success' | 'error';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, tone, message }]);

    // Errors stay longer: they usually carry something the operator has to read and act on,
    // while a success is a confirmation they already expected.
    const lifetime = tone === 'error' ? 8000 : 4000;
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, lifetime);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      <div
        className="pointer-events-none fixed bottom-6 right-6 z-50 flex w-80 flex-col gap-2"
        // Announced to screen readers, since a visual toast alone would be invisible to them.
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={[
              'pointer-events-auto rounded-lg border px-4 py-3 text-body-md',
              toast.tone === 'success'
                ? 'border-status-active/40 bg-surface-secondary text-text-primary'
                : 'border-status-error/40 bg-surface-secondary text-text-primary',
            ].join(' ')}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider');
  }
  return context;
}
